import { repairLine } from "./rule-catalog";
import {
  exposureZoneAt,
  isValidExposureBoundary,
  resolveExposureMarkIndexes,
} from './index';
import type { QuestionnaireValidationIssue } from './index';
import { lanesRepeat, stimulusAssetsOf } from './exposure-coherence';
import { productVersionLabel } from './product-version-label';
import { respondentWordingFor } from './scale-wording-packs';
import { questionAssetSurfaces } from './question-asset-surfaces';
import { studySpecOf } from './study-spec';
import { repeatingLaneAssetIds } from '../study-design';

type RecordLike = Record<string, any>;
const record = (value: unknown): RecordLike =>
  value && typeof value === 'object' && !Array.isArray(value) ? value : {};
const list = (value: unknown): Array<RecordLike> =>
  Array.isArray(value) ? value.filter(v => v && typeof v === 'object') : [];

/**
 * The ref roles that let a question in a lane that runs once show its asset:
 * a stimulus or comparison ref, or a legacy ref with no role. An answer's or a
 * grid row's ref names what is answered about, not something to show
 * (survey-engine `AssetStimulusResolver._ref_authorizes_lane_exposure`).
 */
const LANE_EXPOSURE_ROLES = new Set<string | null>([null, '', 'stimulus', 'comparison']);

const isRecalled = (question: RecordLike): boolean =>
  record(question.assetBinding).view === 'recalled';

/**
 * Whether a shared-view question shows the stimulus: not recalled, and not
 * bound to no asset (`mode: "none"` with no tie to any catalog asset, so a
 * question about one subject or a roster of the versions still shows them).
 * `testedAssets` is the whole catalog: a tie to a brand or pain counts too.
 */
function onScreenInSharedView(question: RecordLike, testedAssets: unknown): boolean {
  if (isRecalled(question)) return false;
  if (record(question.assetBinding).mode !== 'none') return true;
  return questionAssetSurfaces(question, testedAssets).length > 0;
}

/** A lane question the runner scopes to each lane's asset: one bound to the
 * tested assets (survey-engine `monadic.build_sub_survey`). An unbound one is
 * not scoped: each lane pass shows what its own refs name, as a lane that
 * runs once does. */
const BOUND_MODES = new Set<unknown>(['per_asset', 'all_assets']);

/** What twins are shown: the repeating lanes, and each question's screen. */
export interface TwinExposure {
  /** The assets the repeating lane phase runs one isolated pass for; empty when the lane runs once. */
  lanes: Array<string>;
  /** By question stableKey, the stimulus the question puts on screen, in catalog order. */
  onScreen: Map<string, Array<string>>;
  /**
   * The question stableKeys a repeating lane scopes to each pass's asset: each
   * is asked once per asset in `lanes`, with only that one on screen.
   */
  scoped: Set<string>;
}

/**
 * What twins are shown, by the runner's delivery contract over the draft's
 * declared phases (survey-engine `AssetStimulusResolver._asset_ids_for_question`
 * over the passes `monadic.respondent_passes` runs;
 * `tests/fixtures/study-spec/twin-exposure-cases.json` holds both languages
 * to one table, per question). Only the stimulus is ever shown
 * (`stimulusAssetsOf`; the compile stamps each catalog item's role for the
 * engine): a pain or brand is named in questions and answers, never shown.
 *
 * - `lanes`: the assets a repeating lane phase (`lanesRepeat`) runs one
 *   isolated pass for (`repeatingLaneAssetIds`, engine
 *   `monadic.monadic_lane_asset_ids`): every stimulus asset of the types the
 *   lane questions bind, or all of them when they bind no stimulus type (a
 *   lane phase holding only a pain grid repeats over the concepts). Empty when
 *   the lane runs once.
 * - `onScreen`: by question stableKey, the stimulus the question puts on
 *   screen, over every pass and in catalog order (a shared view's order is
 *   each twin's own, `twin_stimulus_order`):
 *   - before the reveal, nothing;
 *   - a question recalled after exposure (`assetBinding.view: "recalled"`),
 *     nothing, wherever it sits;
 *   - in the shared view, every stimulus asset, whatever the question
 *     references, unless it is bound to no asset (`mode: "none"` naming no
 *     asset anywhere; engine `AssetStimulusResolver._shown_in_shared_view`);
 *   - in a repeating lane, each lane's asset on a bound question (the lane
 *     scopes it); an unbound question is not scoped, so it shows what it
 *     shows in a lane that runs once (most name nothing);
 *   - in a lane that runs once, the stimulus its stimulus and comparison refs
 *     name (question-level or on an answer, and an `all_assets` binding's,
 *     which the compile stamps as stimulus refs; a roster's `choice` refs and
 *     a grid's row refs name answers, not stimulus). A bound question naming
 *     none shows the lane's asset, as a repeating lane does: the study's one
 *     stimulus, when it has one (a pain grid after a single concept's reveal
 *     is asked with the concept; NA-09 Q8). With several, it names which.
 *
 * Null for a draft with no declared boundary: its delivery is decided when the
 * boundary is resolved, and the readiness, compile and launch inputs always
 * carry the resolved one (`normalizeQuestionnaireCompileInput`).
 */
export function twinExposureOf(draft: unknown): TwinExposure | null {
  const source = record(draft);
  if (!isValidExposureBoundary(source.exposureBoundary)) return null;
  const questions = list(source.researchQuestions);
  const stimulus = stimulusAssetsOf(source);
  const stimulusIds = stimulus.assets.map(asset => asset.id);
  const marks = resolveExposureMarkIndexes(questions, source.exposureBoundary);
  const zones = questions.map((_, index) => exposureZoneAt(index, marks));
  const repeats = lanesRepeat(questions, marks, studySpecOf(source)?.resolved.design === 'twin_monadic');
  const lanes = repeats
    ? repeatingLaneAssetIds(
      list(record(source.testedAssets).assets).map(asset => ({
        id: String(asset.id ?? ''),
        type: String(asset.type ?? ''),
        stimulus: stimulusIds.includes(asset.id),
      })),
      questions
        .filter((question, index) => zones[index] === 'lane' && !isRecalled(question))
        .flatMap(question => {
          const bound = record(question.assetBinding).assetTypes;
          return Array.isArray(bound) ? bound : [];
        }),
    )
    : [];
  const laneOnce = (question: RecordLike): Array<string> => {
    const named = new Set<string>();
    questionAssetSurfaces(question, stimulus).forEach(surface => {
      if (
        (surface.kind === 'ref' ||
          (surface.kind === 'item' && (surface.list === 'options' || surface.list === 'choiceAssetRefs'))) &&
        LANE_EXPOSURE_ROLES.has(surface.role)
      ) {
        named.add(surface.assetId);
      }
    });
    if (named.size > 0) return stimulusIds.filter(id => named.has(id));
    return BOUND_MODES.has(record(question.assetBinding).mode) && stimulusIds.length === 1
      ? stimulusIds
      : [];
  };
  const scoped = new Set<string>();
  const onScreenOf = (question: RecordLike, index: number): Array<string> => {
    if (zones[index] === 'pre' || isRecalled(question)) return [];
    if (zones[index] === 'shared_view') {
      return onScreenInSharedView(question, source.testedAssets) ? stimulusIds : [];
    }
    if (repeats && BOUND_MODES.has(record(question.assetBinding).mode)) {
      scoped.add(String(question.stableKey));
      return lanes;
    }
    return laneOnce(question);
  };
  return {
    lanes,
    onScreen: new Map(questions.map((question, index) => [String(question.stableKey), onScreenOf(question, index)])),
    scoped,
  };
}

/**
 * A read asked with the stimulus off screen (`assetBinding.view: "recalled"`)
 * comes after a question that puts the stimulus on screen (`twinExposureOf`):
 * the engine has no separate exposure screen, so a recall asked before any
 * question showed the stimulus reads nothing (AD-12). Every write path reaches
 * it through the draft validator (readiness, compile, launch); PLAN checks the
 * same rule before AUTHOR (`_validate_recalled_reads`, one catalog entry).
 */
export function recalledReadIssues(draft: unknown): Array<QuestionnaireValidationIssue> {
  const source = record(draft);
  const exposure = twinExposureOf(source);
  if (!exposure) return [];
  const issues: Array<QuestionnaireValidationIssue> = [];
  let shown = false;
  list(source.researchQuestions).forEach((question, index) => {
    if (isRecalled(question) && !shown) {
      issues.push({
        path: `researchQuestions[${index}].assetBinding.view`,
        code: 'exposure_recalled_before_reveal',
        severity: 'error',
        message: repairLine('exposure_recalled_before_reveal', { topicKey: String(question.stableKey) }),
      });
    }
    if ((exposure.onScreen.get(String(question.stableKey)) ?? []).length > 0) shown = true;
  });
  return issues;
}

/**
 * The tested assets some twin is shown (`twinExposureOf`): each repeating
 * lane's asset, and whatever a question puts on screen.
 */
export function testedAssetsShownToTwins(draft: unknown): Set<string> | null {
  const exposure = twinExposureOf(draft);
  return exposure && new Set([...exposure.lanes, ...[...exposure.onScreen.values()].flat()]);
}

/**
 * A tested version no twin would ever be shown (owner decision 2026-09-25: a
 * study never launches with an asset that no question covers). Adding an asset
 * keeps the recorded design and rewrites no question: a shared view, a
 * repeating lane of its type or a question that shows it takes it in; a
 * question written about one other asset does not. Read over the study's
 * stimulus (`stimulusAssetsOf`) against `testedAssetsShownToTwins`; wording is
 * not read. Before the first question there is nothing to cover: the count
 * rule owns an empty questionnaire.
 *
 * Error severity. The write gate lets only the write that adds the asset
 * commit with it (the backend's `causedByAddedAssets`), while readiness,
 * compile and launch wait for a question about it (or its removal); any other
 * write that leaves a tested asset out is refused.
 */
export function testedAssetUnusedIssues(draft: unknown): Array<QuestionnaireValidationIssue> {
  const source = record(draft);
  const spec = studySpecOf(source);
  if (!spec || !list(source.researchQuestions).length) return [];
  const shown = testedAssetsShownToTwins(source);
  if (!shown) return [];
  const stimulus = new Map(stimulusAssetsOf(source).assets.map(asset => [asset.id, asset]));
  const issues: Array<QuestionnaireValidationIssue> = [];
  const wording = respondentWordingFor(
    typeof source.contentLanguage === 'string' ? source.contentLanguage : undefined,
  );
  spec.resolved.materials.forEach((material, index) => {
    const asset = stimulus.get(material.assetId);
    if (!asset || shown.has(material.assetId)) return;
    const assetName = productVersionLabel(
      typeof asset.name === 'string' ? asset.name : '',
      asset.price,
      wording,
    );
    issues.push({
      path: `studySpec.resolved.materials[${index}]`,
      code: 'tested_asset_unused',
      severity: 'error',
      message: repairLine('tested_asset_unused', { assetName }),
      assetId: material.assetId,
      // A priced product is named by its one version label ("name · price"),
      // so the price version no question shows is told from its siblings.
      params: { assetName },
    });
  });
  return issues;
}

/**
 * The asset ids a verdict's `tested_asset_unused` issues name, from each
 * issue's typed `assetId` (the path is for display). A reader of readiness
 * (the write gate, the builder's "update the questions?" card, the issue copy
 * that names the asset) asks this instead of re-deriving coverage.
 */
export function testedAssetUnusedAssetIds(
  issues: ReadonlyArray<Pick<QuestionnaireValidationIssue, 'code' | 'assetId'>>,
): Set<string> {
  return new Set(
    issues.flatMap(issue =>
      issue.code === 'tested_asset_unused' && issue.assetId ? [issue.assetId] : [],
    ),
  );
}
